import React, { useEffect, useRef } from 'react';
import ResizableTextarea from 'react-textarea-autosize';
import { formatInTimeZone } from 'date-fns-tz';
import {
  InstagramIcons
} from './chat-icons';
import './instagram-chat.css';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { renderTextWithLinks } from '@/lib/linkify';

type Author = 'contact' | 'me';

type ChatMessage = {
  id: string;
  text: string;
  author: Author;
  source: string;
  sending: boolean;
  sent_at: string; // ISO timestamp
  attachments: unknown[];
  isTyping?: boolean;
};

type BubbleGroup = 'single' | 'start' | 'middle' | 'end';

function VerifiedIcon() {
  return (
    <svg className="relative" fill="rgb(0, 149, 246)" height="12" role="img" viewBox="0 0 40 40" width="12"><title>Verified</title><path d="M19.998 3.094 14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094Zm7.415 11.225 2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18Z" fillRule="evenodd"></path></svg>
  );
}


interface InstagramChatProps {
  messages: ChatMessage[];
  input: string;
  setInput: (value: string) => void;
  onSend: () => void;
}

function TypingIndicator() {
  return (
    <div className="flex gap-1 py-1">
      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
    </div>
  );
}

const InstagramMessagesView = React.memo(function InstagramMessagesView({ messages, containerRef, currentChat }: { messages: ChatMessage[]; containerRef: React.RefObject<HTMLDivElement | null>; currentChat: { name: string; username: string; avatar: string; online: boolean } }) {
  const prevMessageCount = useRef(messages.length)
  const animatedIdsRef = useRef<Set<string>>(new Set())
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (containerRef.current && messages.length > 0) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
    prevMessageCount.current = messages.length
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (containerRef.current && messages.length > prevMessageCount.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
    prevMessageCount.current = messages.length
  }, [messages, containerRef])

  useEffect(() => {
    if (!containerRef.current) return
    if (observerRef.current) {
      observerRef.current.disconnect()
    }
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const el = entry.target as HTMLElement
        const id = el.dataset.messageId
        if (id && !animatedIdsRef.current.has(id)) {
          animatedIdsRef.current.add(id)
          el.classList.add('instagram-message-animate')
          observerRef.current?.unobserve(el)
        }
      })
    }, { root: containerRef.current, threshold: 0.1 })

    return () => observerRef.current?.disconnect()
  }, [messages.length, containerRef])

  const setMessageEl = (id: string) => (el: HTMLDivElement | null) => {
    if (!el) return
    el.dataset.messageId = id
    if (!animatedIdsRef.current.has(id)) {
      observerRef.current?.observe(el)
    }
  }

  const parseDate = (message: ChatMessage) => new Date(message.sent_at).getTime()
  const getSender = (message: ChatMessage) => (message.author === 'contact' ? 'contact' : 'me')
  const areGrouped = (a?: ChatMessage, b?: ChatMessage) => {
    if (!a || !b) return false
    return a.author === b.author
  }
  const computeGroup = (index: number): BubbleGroup => {
    const prev = index > 0 ? messages[index - 1] : undefined
    const curr = messages[index]
    const next = index < messages.length - 1 ? messages[index + 1] : undefined
    if (!curr) return 'single'
    const joinsPrev = prev ? prev.author === curr.author : false
    const joinsNext = next ? next.author === curr.author : false
    if (!joinsPrev && !joinsNext) return 'single'
    if (!joinsPrev && joinsNext) return 'start'
    if (joinsPrev && joinsNext) return 'middle'
    return 'end'
  }
  const formatTime = (iso: string) => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      return formatInTimeZone(new Date(iso), tz, 'HH:mm')
    } catch { return '' }
  }
  const getMessageStyle = (sender: 'contact' | 'me', group: BubbleGroup) => {
    const baseStyle = sender === 'contact'
      ? 'bg-[#3797F0] text-white'
      : 'bg-[rgb(239,239,239)] text-gray-1000';

    let radiusStyle = 'rounded-[18px]';

    if (sender === 'contact') {
      if (group === 'start') radiusStyle = 'rounded-3xl rounded-br-sm';
      else if (group === 'middle') radiusStyle = 'rounded-l-3xl rounded-r-sm';
      else if (group === 'end') radiusStyle = 'rounded-3xl rounded-tr-sm';
    } else {
      if (group === 'start') radiusStyle = 'rounded-3xl rounded-bl-sm';
      else if (group === 'middle') radiusStyle = 'rounded-r-3xl rounded-l-sm';
      else if (group === 'end') radiusStyle = 'rounded-3xl rounded-tl-sm';
    }

    return `${baseStyle} ${radiusStyle}`;
  }
  const shouldShowTime = (index: number) => {
    const curr = messages[index] as ChatMessage
    const prev = index > 0 ? (messages[index - 1] as ChatMessage) : undefined
    if (!prev) return true
    const minutesDiff = Math.abs(parseDate(curr) - parseDate(prev)) / (60 * 1000)
    return minutesDiff > 15
  }
  const shouldShowAvatar = (index: number) => {
    const message = messages[index]
    if (!message) return false
    if (getSender(message) !== 'me') return false
    const group = computeGroup(index)
    if (group === 'end' || group === 'single') return true
    const next = index < messages.length - 1 ? messages[index + 1] : undefined
    if (!next) return true
    if (getSender(next) !== 'me') return true
    return false
  }
  const getSpacing = (index: number) => {
    const prev = index > 0 ? messages[index - 1] : undefined
    const curr = messages[index]
    if (!curr) return 'mt-4'
    if (!prev) return 'mt-4'
    return prev.author === curr.author ? 'mt-0.5' : 'mt-4'
  }

  return (
    <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y px-4 py-2 pb-5 bg-white instagram-chat-container">
      <div className="max-w-3xl mx-auto min-w-0">
        {messages.map((message, index) => {
          const spacing = getSpacing(index)
          const showTime = shouldShowTime(index)
          const sender = getSender(message)
          const group = computeGroup(index)
          return (
            <div key={message.id}>
              {showTime && (
                <div className="flex items-center justify-center my-3 animate-fadeIn">
                  <p className="text-xs text-gray-400" suppressHydrationWarning>{formatTime(message.sent_at)}</p>
                </div>
              )}
              <div ref={setMessageEl(message.id)} data-message-id={message.id} className={`${showTime ? '' : spacing} flex items-end ${sender === 'contact' ? 'justify-end' : 'justify-start'}`}>
                {sender === 'me' && (
                  <div className="w-6 mr-2 flex-shrink-0 flex items-end">
                    {shouldShowAvatar(index) && (
                      <Image src={currentChat.avatar} alt={currentChat.name} width={24} height={24} className="rounded-full object-cover" sizes="24px" />
                    )}
                  </div>
                )}
                <div className={`max-w-xs ${sender === 'contact' ? 'order-2' : ''}`}>
                  <div className={`px-3 py-[7px] ${getMessageStyle(sender as 'contact' | 'me', group)} ${message.isTyping ? 'instagram-typing-bubble' : ''}`}>
                    {message.isTyping && !message.text ? (
                      <TypingIndicator />
                    ) : (
                      <p className="text-[15px] leading-[20px] break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{renderTextWithLinks(message.text)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}, (prev, next) => prev.messages === next.messages)

export const InstagramChat: React.FC<InstagramChatProps> = ({ messages, input, setInput, onSend }) => {
  const tCommon = useTranslations('chat.common');
  const tIg = useTranslations('chat.instagram');
  const currentChat = {
    name: "Rama Castro",
    username: "ramacastro",
    avatar: "/agent/ramacastro-144.avif",
    online: true
  };

  const chatContainerRef = useRef<HTMLDivElement>(null)

  // On input focus on mobile, ensure latest messages remain visible (accounting for keyboard)
  const handleFocus = () => {
    if (chatContainerRef.current) {
      // Use a microtask to allow layout to settle after keyboard animation
      setTimeout(() => {
        chatContainerRef.current!.scrollTop = chatContainerRef.current!.scrollHeight
      }, 50)
    }
  }



  const getMessageStyle = (sender: 'contact' | 'me', group: BubbleGroup) => {
    const baseStyle = sender === 'contact'
      ? 'bg-[#3797F0] text-white'
      : 'bg-[rgb(239,239,239)] text-gray-1000';

    let radiusStyle = 'rounded-[18px]';

    if (sender === 'contact') {
      if (group === 'start') radiusStyle = 'rounded-3xl rounded-br-sm';
      else if (group === 'middle') radiusStyle = 'rounded-l-3xl rounded-r-sm';
      else if (group === 'end') radiusStyle = 'rounded-3xl rounded-tr-sm';
    } else {
      if (group === 'start') radiusStyle = 'rounded-3xl rounded-bl-sm';
      else if (group === 'middle') radiusStyle = 'rounded-r-3xl rounded-l-sm';
      else if (group === 'end') radiusStyle = 'rounded-3xl rounded-tl-sm';
    }

    return `${baseStyle} ${radiusStyle}`;
  };

  return (
    <div className="flex size-full w-full min-w-0 bg-white text-gray-900">
      {/* Chat View */}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Chat Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Image
              src={currentChat.avatar}
              alt={currentChat.name}
              width={40}
              height={40}
              className="rounded-full object-cover"
              sizes="40px"
            />
            <div>
              <h2 className="font-semibold text-gray-900 text-base flex items-center gap-1">{currentChat.name} <VerifiedIcon /></h2>
              <p className="text-xs text-gray-500">
                {currentChat.online ? tIg('activeNow') : tIg('active1hAgo')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <InstagramIcons.phone className="w-6 h-6 text-gray-800 cursor-pointer hidden md:block" />
            <InstagramIcons.video className="w-6 h-6 text-gray-800 cursor-pointer hidden md:block" />
            <InstagramIcons.info className="w-5 h-5 text-gray-800 cursor-pointer" />
          </div>
        </div>
        {/* Messages */}
        <InstagramMessagesView messages={messages} containerRef={chatContainerRef} currentChat={currentChat} />

        {/* Message Input */}
        <div className="px-4 py-3 border-t border-gray-200 bg-white">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3">

              <div className="flex-1 min-w-0 flex items-center gap-3 bg-white border border-gray-300 rounded-full px-3 py-2 min-h-11">
                <InstagramIcons.emoji className="w-6 h-6 text-gray-900 cursor-pointer" />
                <ResizableTextarea
                  placeholder={tIg('placeholder')}
                  className="flex-1 min-w-0 bg-transparent outline-none text-[16px] placeholder-gray-400 text-gray-900 resize-none leading-[20px] max-h-36"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onFocus={handleFocus}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSend();
                    }
                  }}
                  minRows={1}
                />

                {
                  input ?
                    <button onClick={onSend} className="text-gray-900 cursor-pointer shrink-0 font-semibold text-sm transition-all duration-200 hover:opacity-70 active:scale-95 animate-sendButtonPop">
                      {tCommon('send')}
                    </button>
                    :
                    <>

                      <InstagramIcons.microphone className="w-6 h-6 text-gray-900 cursor-pointer hidden md:block" />
                      <InstagramIcons.image className="w-6 h-6 text-gray-900 cursor-pointer" />
                      <InstagramIcons.sticker className="w-6 h-6 text-gray-900 cursor-pointer" />
                      <InstagramIcons.heart className="w-6 h-6 text-gray-900 cursor-pointer" />
                    </>
                }

              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
